import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useReducer } from "react";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../utils/environment";
import { LensConfigurationService } from "../../../../service/validDataService";
import useAbortController from "../../../../hooks/useAbortController";
import useDialogModal from "../../../../hooks/useDialogModal";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "../../../../utils/validLookup";
import ValidLensTreatmentInputForm from "./validLensTreatmentInputForm";
import EditValidLensTreatmentForm from "./editValidLensTreatmentForm";
import { validLensTreatmentColumns } from "./columns";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopy from "@mui/icons-material/ContentCopy";

export default function ValidLensTreatment() {
    const [ValidLensTreatmentInputDialog, showValidLensTreatmentInputForm] = useDialogModal(ValidLensTreatmentInputForm);
    const [ValidLensTreatmentEditDialog, showValidLensTreatmentEditForm] = useDialogModal(EditValidLensTreatmentForm);

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidLensTreatmentInputForm();
    }

    const handleRowEdit = (rowData) => {
        showValidLensTreatmentEditForm(null, { recordToEdit: rowData });
    }

    const handleRowCopy = (rowData) => {
        showValidLensTreatmentEditForm(null, { recordToEdit: rowData, isCopy: true });
    }

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        LensConfigurationService.getTreatmentsWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.rows,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal]);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await LensConfigurationService.deleteTreatment(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = (newRecord) => {
        const newData = state.data.concat(newRecord);
        updateState({ data: newData });
    }

    const recordEditHandler = (record) => {
        const existingRecords = state.data;
        const updatesList = existingRecords.map((row) => {
            if (row.id === record.id) {
                return { ...row, ...record };
            }
            return row;
        });

        updateState({ data: updatesList });
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Configuration for Lens Treatments</Typography>
            <Divider sx={{ mb: 2 }} />
            <EnhancedTable
                columns={validLensTreatmentColumns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    {
                        icon: <ContentCopy />,
                        onClick: handleRowCopy
                    },
                ]}
            />
            <ValidLensTreatmentInputDialog onRecordAdded={recordAddHandler} />
            <ValidLensTreatmentEditDialog onRecordEdit={recordEditHandler} />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    );
}
